import { Theme, alpha } from '@mui/material/styles';

/**
 * Soft backgrounds written for a white page are near-white, so in the dark theme
 * (theme text is then light) they would put light text on a pale panel. `tint`
 * keeps the light value in the light theme and swaps in a translucent version of
 * the same hue in the dark one. Self-contained pills that set their own text
 * colour beside their background (chips) do not need it.
 *
 * Keys are the light backgrounds from lib/brand.ts (every TONE.*.bg and ACCENT.*.soft);
 * brand.test.ts checks none is missing. Values are the main hue of each.
 */
const DARK: Record<string, string> = {
  '#e6f2f0': '#146152', // TONE.good.bg       → forest green
  '#fff9e6': '#D4AC0D', // TONE.warn.bg       → warm gold
  '#f9e6eb': '#7D1935', // TONE.bad.bg, ACCENT.quill.soft → burgundy
  '#e6f2f5': '#1A5F7A', // TONE.info.bg, ACCENT.lifescribe.soft → deep teal
  '#f3f4f6': '#6B7280', // TONE.neutral.bg    → gray 500
  '#111827': '#6B7280', // TONE.strong.bg (already dark; a muted grey wash in dark mode)
};
const AMOUNT: Record<string, number> = { '#e6f2f5': 0.2, '#f9e6eb': 0.2 };

export const DARK_KEYS = Object.keys(DARK);

export const tint = (light: string) => (t: Theme): string => {
  if (t.palette.mode !== 'dark') return light;
  const key = light.toLowerCase();
  const base = DARK[key] || '#6B7280';
  return alpha(base, AMOUNT[key] || 0.15);
};

/** Dark text on a soft light panel becomes a light tint of the same hue in the dark theme. */
export const ink = (dark: string, onDark = '#E6F2F5') => (t: Theme): string => (t.palette.mode === 'dark' ? onDark : dark);
